import './style.scss'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import utility from 'src/utils/utility'
import AuthApiContext, { AuthApiProvider } from 'src/provider/API/v2/call-service/Auth'
import ConfigurationApiContext, { ConfigurationApiProvider } from 'src/provider/API/v2/call-service/configuration'
import { AppText, CustomContainer, FlexColumn, FlexRow, Form } from 'src/style'
import { Logo } from 'src/assets/icons'
import AuthenticationWorld from 'src/assets/v2/img/authentication_world.svg'
import { BsEye, BsEyeSlash } from 'react-icons/bs'
import { ChangePasswordModal, type ChangePasswordValue, TextField, Box } from 'src/componentv2'
import { EMAIL_REGEX, isValidCode, type LoginInput } from 'src/componentv2'
import { OTPVerification2 } from 'src/app/preAuth2/forgetPassword2'

const EYE_ICON_STYLE = {
    position: 'absolute' as const,
    right: '1.6rem',
    top: '3.8rem',
    cursor: 'pointer',
    color: 'rgba(0, 0, 0, 0.5)',
    fontSize: '1.7rem',
}

const SuperAdminSetupForm = () => {
    const navigate = useNavigate()
    const { login, validateLoginOtp } = useContext(AuthApiContext)
    const { changePassword } = useContext(ConfigurationApiContext)

    const [input, setInput] = useState<LoginInput>({ ibInstitutionCode: '', userID: '', password: '' })
    const [showPassword, setShowPassword] = useState(false)
    const [submitAttempted, setSubmitAttempted] = useState(false)
    const [incorrectPassword, setIncorrectPassword] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const [showOtp, setShowOtp] = useState(false)
    const [otp, setOtp] = useState('')
    const [otpError, setOtpError] = useState(false)
    const [maskedEmail, setMaskedEmail] = useState('')

    const [showPasswordModal, setShowPasswordModal] = useState(false)

    const set = (key: keyof LoginInput) => (value: string) => {
        setInput((prev) => ({ ...prev, [key]: value }))
        if (key === 'password' && incorrectPassword) setIncorrectPassword(false)
    }

    const isUserIdInvalid = () => {
        const value = input.userID.trim()
        if (!value) return true
        if (value.includes('@')) return !EMAIL_REGEX.test(value)
        return false
    }

    const errors = {
        ibInstitutionCode: !isValidCode(input.ibInstitutionCode),
        userID: isUserIdInvalid(),
        password: !input.password,
    }

    const fieldError = (key: keyof LoginInput) => {
        if (key === 'password' && incorrectPassword) return 'Incorrect password. Try again'
        if (!submitAttempted || !errors[key]) return undefined
        if (key === 'ibInstitutionCode') return 'Invalid Code'
        if (key === 'userID') return 'Please enter a valid username'
        return undefined
    }

    async function handleSignIn(e?: React.FormEvent<HTMLButtonElement | HTMLFormElement>) {
        e?.preventDefault()

        const hasErrors = Object.values(errors).some(Boolean)
        if (hasErrors) {
            setSubmitAttempted(true)
            return
        }

        setIsSubmitting(true)
        try {
            const response = await login({ ...input })
            if (!response) {
                setIncorrectPassword(true)
                return
            }
            setOtp('')
            setOtpError(false)
            setMaskedEmail(response.maskedEmail || '')
            setShowOtp(true)
        } finally {
            setIsSubmitting(false)
        }
    }

    async function handleResendOtp() {
        const response = await login({ ...input })
        if (response) setMaskedEmail(response.maskedEmail || '')
    }

    function setOtpValue(value: string) {
        if (value.length > 6) return
        setOtpError(false)
        setOtp(value)
    }

    async function handleOtpVerification() {
        if (!otp || otp.length !== 6) return

        const response = await validateLoginOtp(input.ibInstitutionCode, otp)
        if (response) {
            setShowOtp(false)
            setOtp('')
            setShowPasswordModal(true)
        } else {
            setOtpError(true)
        }
    }

    // submit as soon as all 6 digits are in, rather than waiting on a button
    useEffect(() => {
        if (showOtp && otp.length === 6) handleOtpVerification()
    }, [otp])

    const handleSetPassword = async (value: ChangePasswordValue) => {
        const ok = await changePassword({
            currentPassword: value.oldPassword,
            newPassword: value.newPassword,
        })
        if (ok) {
            localStorage.removeItem('adminAuthToken')
            setShowPasswordModal(false)
            navigate('/signin2')
        }
        return ok
    }

    return (
        <CustomContainer width="100" sizeUnit="%" height="100" hUnit="%">
            <FlexRow alignItems="stretch" justifyContent="flex-start" height="100" hUnit="%">
                <FlexColumn
                    width="50" sizeUnit="%" minHeight="100" mnHUnit="%" padding="2"
                    bgColor="#ffffff" className="login-about-section"
                    style={{
                        position: 'relative', justifyContent: 'center',
                        overflow: 'hidden', backgroundColor: '#ffffff',
                    }}
                >
                    <img
                        src={AuthenticationWorld}
                        alt="Authentication"
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div style={{ position: 'absolute', left: '48px', bottom: '48px', zIndex: 1 }}>
                        <AppText color="#FFFFFF" textSize="2.4" fontWeight="700">Africa's Foremost</AppText>
                        <AppText color="#FFFFFF" textSize="2.4" fontWeight="700">Banking-as-a-Platform</AppText>
                    </div>
                </FlexColumn>

                <FlexColumn
                    width="50" sizeUnit="%" minHeight="100" mnHUnit="%"
                    bgColor="#ffffff" padding="2" className="login-form-section"
                >
                    <CustomContainer width="40" leftMargin="auto" rightMargin="auto" sizeUnit="" className="login-form-container">
                        <Logo className="login-form-logo" />

                        <AppText
                            color='#101828' textSize='3' fontWeight='600' bottomPadding='0.7'
                            className='login-title'
                        >
                            Set up your admin account
                        </AppText>
                        <AppText
                            color='#667085' textSize='1.6'
                            className='login-instruction'
                        >
                            Use the temporary credentials sent to your email to begin your setup
                        </AppText>

                        <Form onSubmit={(e: any) => { e.preventDefault(); handleSignIn(e) }}>
                            <Box topMargin="2.4">
                                <TextField
                                    label="Institution code"
                                    placeholder="Enter Institution code"
                                    value={input.ibInstitutionCode}
                                    onChange={set('ibInstitutionCode')}
                                    type="number"
                                    error={fieldError('ibInstitutionCode')}
                                />
                            </Box>

                            <Box topMargin="1.6">
                                <TextField
                                    label="Username"
                                    placeholder="Enter your username"
                                    value={input.userID}
                                    onChange={set('userID')}
                                    error={fieldError('userID')}
                                />
                            </Box>

                            <Box topMargin="1.6" style={{ position: 'relative' }}>
                                <TextField
                                    label="Temporary Password"
                                    placeholder="Enter your password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={input.password}
                                    onChange={set('password')}
                                    error={fieldError('password')}
                                />
                                <span
                                    style={EYE_ICON_STYLE}
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    { showPassword ? <BsEye/> : <BsEyeSlash/> }
                                </span>
                            </Box>

                            <button type="submit" className="super-admin-setup-form__submit" disabled={isSubmitting}>
                                {isSubmitting ? 'Signing in...' : 'Sign in'}
                            </button>
                        </Form>
                    </CustomContainer>
                </FlexColumn>
            </FlexRow>

            {showOtp &&
                <OTPVerification2
                    close={() => { setShowOtp(false); setOtpError(false) }}
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => setOtpValue(e.target.value)}
                    formData={{ ibInstitutionCode: input.ibInstitutionCode, email: '', otp, newPassword: '', confirmPassword: '' }}
                    handleNext={handleOtpVerification}
                    handleResend={handleResendOtp}
                    error={otpError}
                    maskedEmail={maskedEmail}
                />
            }

            <ChangePasswordModal
                open={showPasswordModal}
                onClose={() => {
                    setShowPasswordModal(false)
                    setInput((prev) => ({ ...prev, password: '' }))
                }}
                onSubmit={handleSetPassword}
            />
        </CustomContainer>
    )
}

const SuperAdminSetup = () => (
    <AuthApiProvider>
        <ConfigurationApiProvider>
            <SuperAdminSetupForm />
        </ConfigurationApiProvider>
    </AuthApiProvider>
)

export default utility.routeData('/admin-setup', 'Admin Setup', <SuperAdminSetup />)